import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Tag, ArrowLeft, User } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import LeadForm from "../components/LeadForm";
import api from "../lib/api";

function mdToHtml(md) {
  if (!md) return "";
  let html = md
    .replace(/^### (.*$)/gim, "<h3>$1</h3>")
    .replace(/^## (.*$)/gim, "<h2>$1</h2>")
    .replace(/^# (.*$)/gim, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/^\- (.*$)/gim, "<li>$1</li>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br/>");
  // wrap lists
  html = html.replace(/(<li>.*?<\/li>)(?!<li>)/gs, "<ul>$1</ul>");
  // tables
  html = html.replace(/\|(.+)\|/g, (m, row) => {
    const cells = row.split("|").map(c => c.trim()).filter(c => c && !/^-+$/.test(c));
    if (cells.length === 0) return "";
    return "<tr>" + cells.map(c => `<td>${c}</td>`).join("") + "</tr>";
  });
  html = html.replace(/(<tr>.*?<\/tr>)+/gs, "<table>$&</table>");
  return `<p>${html}</p>`;
}

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/blogs/${slug}`).then(({ data }) => {
      setBlog(data);
      document.title = data.meta_title || data.title;
      api.get("/blogs", { params: { category: data.category, limit: 4 } }).then(({ data: rel }) => {
        setRelated(rel.filter(r => r.slug !== slug).slice(0, 3));
      });
    }).catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <div className="pt-40 pb-20 text-center">
      <h1 className="h2 mb-4">Article not found</h1>
      <Link to="/blog" className="text-[#0F52BA] font-semibold">← Back to blog</Link>
    </div>
  );

  if (!blog) return <div className="pt-40 pb-20 text-center text-[#5C7368]">Loading...</div>;

  return (
    <div data-testid="blog-detail-page">
      <SEO page="blog" titleFallback={blog.title} descFallback={blog.meta_description || blog.excerpt}/>

      <article className="pt-32 pb-16 bg-white">
        <div className="container-custom max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-[#5C7368] hover:text-[#0A1F15] mb-6">
            <ArrowLeft size={14}/> All articles
          </Link>
          <div className="flex items-center gap-3 text-xs text-[#5C7368] mb-4">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#135934]/10 text-[#135934] font-bold uppercase tracking-wider"><Tag size={12}/>{blog.category}</span>
            {blog.created_at && <span className="inline-flex items-center gap-1"><Calendar size={12}/>{new Date(blog.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric"})}</span>}
            <span className="inline-flex items-center gap-1"><User size={12}/>{blog.author}</span>
          </div>
          <h1 className="h1 mb-6">{blog.title}</h1>
          <p className="text-xl text-[#2D4739] leading-relaxed mb-10">{blog.excerpt}</p>
          {blog.cover_image && (
            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10">
              <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover"/>
            </div>
          )}
          <div
            className="prose-solar"
            dangerouslySetInnerHTML={{ __html: mdToHtml(blog.content) }}
            data-testid="blog-content"
          />
        </div>
      </article>

      <section className="section-padding bg-[#F9F8F6]">
        <div className="container-custom max-w-3xl">
          <div className="bg-[#135934] rounded-3xl p-10 text-white">
            <h3 className="font-display font-extrabold text-3xl mb-3">Ready to go solar?</h3>
            <p className="text-white/85 mb-6">Get a free site survey and personalized quote from Orax Solar.</p>
            <div className="bg-white rounded-2xl p-5">
              <LeadForm source={`blog-${slug}`}/>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="pb-20 bg-[#F9F8F6]">
          <div className="container-custom">
            <h3 className="h3 mb-6">Related articles</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map(r => (
                <Link to={`/blog/${r.slug}`} key={r.id} className="group bg-white rounded-2xl overflow-hidden border border-[#E5E3DB] card-lift">
                  <div className="aspect-[16/10] overflow-hidden">
                    {r.cover_image && <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>}
                  </div>
                  <div className="p-5">
                    <div className="text-xs font-bold uppercase tracking-wider text-[#135934] mb-2">{r.category}</div>
                    <h4 className="font-display font-bold text-lg text-[#0A1F15]">{r.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
